// hooks/useCreateNewReservation.ts
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../store";
import { CreateParcelCustomerReservationData } from "../../api/types";
import Logger from "../helpers/logger";
import ErrorTracker from "../helpers/errorTracker";
import { useCreateParcelCustomerReservationMutation } from "../../api/reservations/reservationsApiSlice";
import { processReservationData } from "./processors";
import {
  reservationCreated,
  bookingStepChanged,
} from "../slice/bookingCreation/bookingCreationSlice";
import { BookingCreationStep } from "../../modules/bookLocation/parcel/BookLocationPage";
import { enqueueNotification } from "../slice/notification/notificationSlice";
import KolulaStorageManager from "../../storage/KolulaStorageManager";
import { t } from "i18next";

export const useCreateNewReservation = (useCase?: string) => {
  const indexDBStore = new KolulaStorageManager();
  const hasSkippedLockeSelection = useSelector(
    (state: RootState) => state.booking.hasSkippedLockeSelection
  );
  const { selectedLocation, selectedGroup, personalData, orderData } =
    useSelector((state: RootState) => state.bookingCreation);

  const [createNewReservationMutation] =
    useCreateParcelCustomerReservationMutation();
  const dispatch = useDispatch();

  const createNewReservation = async () => {
    try {
      if (!selectedLocation || !personalData || !orderData) {
        return;
      }

      // Build base reservation data
      const reservationData: CreateParcelCustomerReservationData = {
        service: "parcel",
        location: selectedLocation.id,
        contactData: {
          firstname: personalData.firstName,
          lastname: personalData.lastName,
          email: personalData.email,
          telephone: personalData.phone,
        },
        metadata: {},
      };

      // Conditionally add metadata if locker selection is not skipped
      if (!hasSkippedLockeSelection && selectedGroup) {
        reservationData.metadata = {
          locker: selectedGroup.id,
          delivery: orderData.date?.format("YYYY-MM-DD"),
        };
      }

      // Submit reservation
      const response = await createNewReservationMutation(reservationData).unwrap();

      if (response.id) {
        await indexDBStore.insertInternalMultipleOrdersIds([response.id]);
        dispatch(
          bookingStepChanged({
            step: BookingCreationStep.success,
            nextEnabled: true,
          })
        );
        dispatch(
          enqueueNotification({
            severity: "success",
            message: t("book_location.success.reservation_added"),
          })
        );
      }

      const processedReservation = processReservationData(response);
      if (processedReservation) {
        dispatch(reservationCreated({ reservation: processedReservation }));
      }

      return processedReservation;
    } catch (error) {
      Logger.for("createNewReservation").error(error);
      ErrorTracker.trackException(error as Error);
    }
  };

  return createNewReservation;
};
